import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { WorkItemFormFieldsComponent } from '../work-item-form-fields/work-item-form-fields.component';
import {
  buildWorkItemFormControls,
  composeShopHours,
  decomposeShopHours,
  isFlatMode,
  parseMoney,
  PricingMode
} from '../work-item-form-fields/work-item-form';
import { AlertService } from '../../services/alert.service';
import {
  ImageTypeResponseDto,
  InvoiceBoardVehicleDto,
  InvoiceBoardWorkItemDto,
  InvoiceService,
  WorkItemImageResponseDto
} from '../../services/invoice.service';
import { Customer } from '../../models/Customer.model';

// An image staged in the modal, uploaded by the opener once the work item id
// is known (mirrors the add-vehicle-modal pattern).
export interface PendingWorkItemImage {
  file: File;
  imageTypeId: string;
  previewUrl: string;
}

/**
 * Emitted via modalRef.close() when the form is saved. The opener is
 * responsible for the API calls (and any invoice status regression warning),
 * since those rules depend on the invoice's current status.
 */
export interface WorkItemModalResult {
  mode: 'create' | 'edit' | 'delete';
  invoiceVehicleId: string;
  workItemId?: string;
  // True when shop time or parts pricing changed (monetary values).
  monetaryChanged: boolean;
  // Edit mode: clear the customer rejection when saving.
  clearRejection?: boolean;
  // Staged images to upload once the save returns the WorkItemId.
  pendingImages?: PendingWorkItemImage[];
  // Absent for 'delete'.
  data?: {
    Title: string;
    EmpUserId: string | null;
    Miles?: number;
    ShopHours?: number;
    PartsPricing?: number;
    // null = no override / clear an existing override.
    FlatFee: number | null;
    FlatFeeTaxable: boolean;
    Notes?: string;
  };
}

@Component({
  selector: 'app-work-item-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, WorkItemFormFieldsComponent],
  templateUrl: './work-item-modal.component.html',
  styleUrls: ['./work-item-modal.component.scss']
})
export class WorkItemModalComponent implements OnInit, OnDestroy {
  @Input() modalRef: NgbModalRef | undefined;
  @Input() vehicles: InvoiceBoardVehicleDto[] = [];
  @Input() mechanics: Customer[] = [];
  // When set, the modal edits this work item; otherwise it creates a new one.
  @Input() workItem: InvoiceBoardWorkItemDto | null = null;
  // Whether the delete button shows (edit mode, invoice still in MECHANIC_REVIEW).
  @Input() allowDelete: boolean = false;
  // View-only mode (e.g. AWAITING_PAYMENT / COMPLETE): fields shown as details, no save.
  @Input() readOnly: boolean = false;
  // Images stay editable through every invoice stage — but only for staff.
  @Input() canEditImages: boolean = false;

  // Attached images + lookup of image types (part-receipt / vehicle-image / other).
  images: WorkItemImageResponseDto[] = [];
  imageTypes: ImageTypeResponseDto[] = [];
  // Images staged locally; the opener uploads them after the save returns the id.
  pendingImages: PendingWorkItemImage[] = [];
  // Set once a save is attempted with a staged image missing its type, so the
  // offending cards can flag themselves; cleared as soon as a type is picked.
  imageValidationAttempted: boolean = false;

  workItemForm: FormGroup = new FormGroup({
    invoiceVehicleId: new FormControl(''),
    clearRejection: new FormControl(false),
    ...buildWorkItemFormControls()
  });

  constructor(
    private alertService: AlertService,
    private invoiceService: InvoiceService
  ) {}

  get isEdit(): boolean {
    return !!this.workItem;
  }

  get isFlatMode(): boolean {
    return isFlatMode(this.workItemForm);
  }

  ngOnInit(): void {
    if (this.workItem) {
      const time = decomposeShopHours(this.workItem.ShopHours);
      this.workItemForm.patchValue({
        invoiceVehicleId: this.workItem.InvoiceVehicleId,
        title: this.workItem.Title,
        empUserId: this.workItem.EmpUserId || '',
        miles: this.workItem.Miles != null ? `${this.workItem.Miles}` : '',
        shopHours: time.hours,
        shopMinutes: time.minutes,
        partsPricing: this.workItem.PartsPricing != null ? `${this.workItem.PartsPricing}` : '',
        flatFee: this.workItem.FlatFee != null ? `${this.workItem.FlatFee}` : '',
        flatFeeTaxable: this.workItem.FlatFeeTaxable === true,
        notes: this.workItem.Notes || ''
      });

      // Attached images only exist for saved work items.
      this.loadImages();
    } else if (this.vehicles.length > 0) {
      this.workItemForm.get('invoiceVehicleId')?.setValue(this.vehicles[0].InvoiceVehicleId);
    }

    // Types are needed for staging labels too, so load them in every mode.
    this.loadImageTypes();

    // Pricing mode: preload from an existing override. The shared fields
    // component enables/disables the mode's controls in its own ngOnInit
    // (which runs after this) and tracks the toggle from there.
    const mode: PricingMode = this.workItem?.FlatFee != null ? 'flat' : 'computed';
    this.workItemForm.get('pricingMode')?.setValue(mode, { emitEvent: false });
  }

  ngOnDestroy(): void {
    for (const pending of this.pendingImages) {
      URL.revokeObjectURL(pending.previewUrl);
    }
  }

  // ============= DISPLAY HELPERS =============

  getVehicleDisplayName(vehicle: InvoiceBoardVehicleDto): string {
    const parts = [vehicle.Year, vehicle.Make, vehicle.Model].filter(p => !!p);
    return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
  }

  // For view-only mode: the mechanic dropdown options may not be loaded for
  // every viewer (customers), so display the assignee from the work item.
  get assignedMechanicLabel(): string {
    if (!this.workItem?.EmpUserId) return 'Unassigned';
    return `${this.workItem.EmpFirstName || ''} ${this.workItem.EmpLastName || ''}`.trim() || 'Unassigned';
  }

  get workItemVehicleLabel(): string {
    const vehicle = this.vehicles.find(v => v.InvoiceVehicleId === this.workItem?.InvoiceVehicleId);
    return vehicle ? this.getVehicleDisplayName(vehicle) : '';
  }

  // Render a decimal hour value as "6 hr 30 min".
  formatShopTime(totalHours: number): string {
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours} hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min`);
    }
    return parts.join(' ') || '0 min';
  }

  // ============= WORK ITEM IMAGES =============

  private loadImages(): void {
    if (!this.workItem) return;
    this.invoiceService.getWorkItemImages(this.workItem.WorkItemId).subscribe({
      next: (images) => this.images = images,
      error: (error) => console.error('[WorkItemModal] Failed to load images:', error)
    });
  }

  private loadImageTypes(): void {
    this.invoiceService.getImageTypes().subscribe({
      next: (types) => this.imageTypes = types,
      error: (error) => console.error('[WorkItemModal] Failed to load image types:', error)
    });
  }

  getImageTypeLabel(imageTypeId: string): string {
    return this.imageTypes.find(t => t.ImageTypeId === imageTypeId)?.ImageType || '';
  }

  /**
   * Handles files from either the file-system picker or the camera input.
   * Stages the image straight into the upload list; the type is chosen from a
   * dropdown on the staged card afterwards.
   */
  onImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this.alertService.showSnackBar('Only JPEG, PNG, GIF, and WebP images are allowed.');
      input.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.alertService.showSnackBar('Image exceeds the 10MB size limit.');
      input.value = '';
      return;
    }

    this.pendingImages = [...this.pendingImages, {
      file,
      imageTypeId: '',
      previewUrl: URL.createObjectURL(file)
    }];
    // Reset so re-selecting the same file still fires the change event.
    input.value = '';
  }

  removePendingImage(index: number): void {
    const pending = this.pendingImages[index];
    if (pending) {
      URL.revokeObjectURL(pending.previewUrl);
    }
    this.pendingImages = this.pendingImages.filter((_, i) => i !== index);
  }

  onImageTypeChange(image: WorkItemImageResponseDto, imageTypeId: string): void {
    this.invoiceService.updateWorkItemImage(image.ImageId, imageTypeId).subscribe({
      next: () => {
        image.ImageTypeId = imageTypeId;
        this.alertService.showSnackBar('Image type updated.');
      },
      error: (error) => {
        console.error('[WorkItemModal] Failed to update image type:', error);
        this.alertService.showSnackBar('Failed to update image type.');
        this.loadImages();
      }
    });
  }

  deleteImage(image: WorkItemImageResponseDto): void {
    this.invoiceService.deleteWorkItemImage(image.ImageId).subscribe({
      next: () => {
        this.images = this.images.filter(i => i.ImageId !== image.ImageId);
        this.alertService.showSnackBar('Image deleted.');
      },
      error: (error) => {
        console.error('[WorkItemModal] Failed to delete image:', error);
        this.alertService.showSnackBar('Failed to delete image.');
      }
    });
  }

  // ============= SUBMIT / DELETE / CANCEL =============

  onSubmit(): void {
    if (!this.workItemForm.get('title')?.valid) {
      this.workItemForm.get('title')?.markAsDirty();
      this.alertService.showSnackBar('Please enter a job title.');
      return;
    }

    const numericControls = ['miles', 'shopHours', 'partsPricing', 'flatFee'];
    for (const control of numericControls) {
      // .invalid, not !.valid: the inactive pricing mode's controls are
      // disabled, and disabled controls are neither valid nor invalid.
      if (this.workItemForm.get(control)?.invalid) {
        this.workItemForm.get(control)?.markAsDirty();
        this.alertService.showSnackBar(`Error with ${control} input`);
        return;
      }
    }

    const invoiceVehicleId = this.workItemForm.get('invoiceVehicleId')?.value;
    if (!invoiceVehicleId) {
      this.alertService.showSnackBar('Please select a vehicle.');
      return;
    }

    // Every staged image needs a type before it can be uploaded.
    if (this.pendingImages.some(p => !p.imageTypeId)) {
      this.imageValidationAttempted = true;
      this.alertService.showSnackBar('Please select an image type for each staged image.');
      return;
    }

    const milesValue = this.workItemForm.get('miles')?.value;
    const shopHours = composeShopHours(this.workItemForm);
    // Disabled controls still expose .value, so the inactive mode's retained
    // inputs flow through as reference data.
    const partsPricing = parseMoney(this.workItemForm.get('partsPricing')?.value);
    // Flat-fee override only applies in flat mode; computed mode always
    // clears any existing override.
    if (this.isFlatMode && parseMoney(this.workItemForm.get('flatFee')?.value) == null) {
      this.alertService.showSnackBar('Please enter a flat fee amount.');
      return;
    }
    const flatFee = this.isFlatMode
      ? parseMoney(this.workItemForm.get('flatFee')?.value) ?? null
      : null;
    const flatFeeTaxable = flatFee != null && this.workItemForm.get('flatFeeTaxable')?.value === true;

    const empUserId = this.workItemForm.get('empUserId')?.value || null;

    // Labor is hours x the assigned mechanic's rate, so swapping mechanics can
    // move the price just like editing it. Only in computed mode (a flat fee
    // overrides labor entirely), only with hours on the clock, and only when
    // the rate actually differs — assigning a previously unassigned item counts,
    // since its old rate reads as 0.
    const newRate = this.mechanics.find(m => m.userId === empUserId)?.hourlyRate ?? 0;
    const laborRateChanged = !this.isFlatMode &&
      (shopHours ?? 0) > 0 &&
      newRate !== (this.workItem?.EmpHourlyRate ?? 0);

    const monetaryChanged = this.isEdit
      ? (shopHours ?? null) !== (this.workItem?.ShopHours ?? null) ||
        (partsPricing ?? null) !== (this.workItem?.PartsPricing ?? null) ||
        (flatFee ?? null) !== (this.workItem?.FlatFee ?? null) ||
        flatFeeTaxable !== (this.workItem?.FlatFeeTaxable ?? false) ||
        laborRateChanged
      : shopHours != null || partsPricing != null || flatFee != null;

    const result: WorkItemModalResult = {
      mode: this.isEdit ? 'edit' : 'create',
      invoiceVehicleId,
      workItemId: this.workItem?.WorkItemId,
      monetaryChanged,
      clearRejection: this.workItemForm.get('clearRejection')?.value === true,
      pendingImages: this.pendingImages,
      data: {
        Title: this.workItemForm.get('title')?.value,
        EmpUserId: empUserId,
        Miles: milesValue !== '' && milesValue !== null ? parseInt(milesValue, 10) : undefined,
        ShopHours: shopHours,
        PartsPricing: partsPricing,
        FlatFee: flatFee,
        FlatFeeTaxable: flatFeeTaxable,
        Notes: this.workItemForm.get('notes')?.value || undefined
      }
    };

    if (this.modalRef) {
      this.modalRef.close(result);
    }
  }

  onDelete(): void {
    if (this.modalRef && this.workItem) {
      // The opener runs its own confirmation before actually deleting.
      this.modalRef.close({
        mode: 'delete',
        invoiceVehicleId: this.workItem.InvoiceVehicleId,
        workItemId: this.workItem.WorkItemId,
        monetaryChanged: false
      } as WorkItemModalResult);
    }
  }

  onCancel(): void {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }
}
